import { readonly, ref } from 'vue'
import type Pusher from 'pusher-js'
import { REALTIME_KEY } from '@/constants/app'
import { http } from '@/services/http'
import { channelsFor, parseHints, type LiveHint } from '@/lib/realtime-protocol'
import type { AuthUser } from '@/types/api'

export type { LiveHint } from '@/lib/realtime-protocol'

/**
 * Live screens: a socket that says "this changed", and the screens that react by asking again.
 *
 * ## What travels
 *
 * Hints, not data. A hint is a topic (`package`, `pickup_session`, ...) and sometimes the id of the
 * thing that moved. It is never a state, a name, a code or an amount: the screen that receives one
 * fetches the object again through the ordinary API, which decides what this person may see. A
 * hint that reached the wrong person would therefore show them an id and the fact that something
 * moved, and their follow-up request would be refused.
 *
 * ## Who listens, and on what
 *
 * Only on the channels made for the signed-in person: their own (`user.<id>` or `platform.<id>`)
 * and, for a courier, the pool of work any courier may take. The server authorizes each one
 * (`POST /api/broadcasting/auth`, with the same bearer token and the same refresh-on-401 as every
 * other request), so a tampered channel name is refused there, not here.
 *
 * ## When it is off
 *
 * No key configured, a role with no live screens, or a socket that cannot connect: nothing breaks.
 * {@link realtimeConnected} says whether the socket is up, and `useLiveRefresh` falls back to a
 * slow poll while it is not.
 */

type HintHandler = (hints: LiveHint[]) => void

const hintHandlers = new Set<HintHandler>()
const resyncHandlers = new Set<() => void>()

const connected = ref(false)
const expected = ref(false)

/** True while the socket is up. Screens poll slowly when it is not. */
export const realtimeConnected = readonly(connected)

/** True from the moment the app set out to connect (a supported role, a key), whether or not it has. */
export const realtimeExpected = readonly(expected)

let client: Pusher | null = null
let startedFor: string | null = null
let generation = 0
let hiddenAt: number | null = null
let visibilityBound = false

/** Back from the background after this long, assume hints were missed and ask for a refresh. */
const RESYNC_AFTER_HIDDEN_MS = 20_000

/** Run `handler` for every batch of hints. Returns the function that stops it. */
export function onLiveHints(handler: HintHandler): () => void {
  hintHandlers.add(handler)

  return () => hintHandlers.delete(handler)
}

/**
 * Run `handler` when hints may have been missed: the socket came back after a drop, or the app
 * returns from the background. Everything on screen should be asked for again.
 */
export function onLiveResync(handler: () => void): () => void {
  resyncHandlers.add(handler)

  return () => resyncHandlers.delete(handler)
}

function emitResync() {
  resyncHandlers.forEach((handler) => handler())
}

/**
 * Ask every live screen to read again now: what a person pulling down to refresh means. It is the
 * same path a reconnect takes, so a screen needs nothing extra to support it.
 */
export function requestLiveRefresh(): void {
  emitResync()
}

function bindVisibility() {
  if (visibilityBound) return
  visibilityBound = true

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      hiddenAt = Date.now()
      return
    }

    const wasAway = hiddenAt === null ? 0 : Date.now() - hiddenAt
    hiddenAt = null

    if (startedFor && wasAway > RESYNC_AFTER_HIDDEN_MS) emitResync()
  })
}

/**
 * Connect for this person, or disconnect when there is none. Safe to call on every change of the
 * session: it does nothing when the same person is already connected.
 */
export async function startRealtime(user: Pick<AuthUser, 'id' | 'role'> | null): Promise<void> {
  const channels = REALTIME_KEY ? channelsFor(user) : []
  const identity = channels.length && user ? `${user.role}:${user.id}` : null

  if (identity === startedFor) return

  stopRealtime()

  if (!identity) return

  startedFor = identity
  expected.value = true
  bindVisibility()

  const mine = ++generation
  const { default: PusherClient } = await import('pusher-js')

  // Signed out, or switched to someone else, while the library was loading.
  if (mine !== generation) return

  const secure = window.location.protocol === 'https:'
  const port = Number(window.location.port) || (secure ? 443 : 80)

  const socket = new PusherClient(REALTIME_KEY, {
    // The gateway serves the socket on the app's own origin under /app, so no extra host or CORS.
    cluster: 'local',
    wsHost: window.location.hostname,
    wsPort: port,
    wssPort: port,
    forceTLS: secure,
    enabledTransports: ['ws', 'wss'],
    disableStats: true,
    channelAuthorization: {
      transport: 'ajax',
      endpoint: '/api/broadcasting/auth',
      // Through the app's own client, so the bearer token is attached and renewed like any other call.
      customHandler: ({ socketId, channelName }, callback) => {
        http
          .post('/broadcasting/auth', { socket_id: socketId, channel_name: channelName }, { baseURL: '/api' })
          .then((response) => callback(null, response.data))
          .catch((error: unknown) => callback(error instanceof Error ? error : new Error('channel refused'), null))
      }
    }
  })

  let hasConnected = false

  socket.connection.bind('state_change', ({ current }: { current: string }) => {
    const up = current === 'connected'

    connected.value = up

    // The first connection has nothing to catch up on; every later one does.
    if (up && hasConnected) emitResync()
    if (up) hasConnected = true
  })

  for (const name of channels) {
    socket.subscribe(name).bind('live.changed', (payload: unknown) => {
      const hints = parseHints(payload)

      if (hints.length) hintHandlers.forEach((handler) => handler(hints))
    })
  }

  client = socket
}

/** Close the socket and forget the person. Called on sign-out and when the session changes. */
export function stopRealtime(): void {
  generation += 1
  startedFor = null
  expected.value = false
  connected.value = false

  if (client) {
    client.disconnect()
    client = null
  }
}
